import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LaneFilterState,
  LaneSummary,
  ShipmentOrderLane,
} from 'src/entities/ShipmentOrderLane';
import {
  useQueryShipmentOrderLanes,
  useQueryShipmentOrderLanesSummary,
} from 'src/hooks/useShipmentOrderLanes';

const DEFAULT_PER_PAGE = 50;

type UseLanesStateParams = {
  startDate: string;
  endDate: string;
};

type UseLanesStateReturn = {
  // データ
  lanes: ShipmentOrderLane[];
  summary: LaneSummary | undefined;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
  };

  // フィルタ状態
  filters: LaneFilterState;

  // 詳細シート状態
  selectedLane: ShipmentOrderLane | null;
  isDetailOpen: boolean;

  // ローディング状態
  isLoading: boolean;
  isSummaryLoading: boolean;

  // エラー状態
  isError: boolean;
  refetch: () => void;

  // アクション
  updateFilters: (newFilters: Partial<LaneFilterState>) => void;
  setPage: (page: number) => void;
  openDetail: (lane: ShipmentOrderLane) => void;
  closeDetail: () => void;
  handleDateRangeChange: (startOn: string, endOn: string) => void;
};

/**
 * レーン管理画面の状態管理フック
 */
export const useLanesState = ({
  startDate,
  endDate,
}: UseLanesStateParams): UseLanesStateReturn => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // フィルタ状態
  const [filters, setFilters] = useState<LaneFilterState>({
    loadingStartOn: startDate,
    loadingEndOn: endDate,
    planningStatus: undefined,
    deliveryMethod: undefined,
    keyword: undefined,
    page: 1,
    per: DEFAULT_PER_PAGE,
  });

  // 詳細シート状態
  const [selectedLane, setSelectedLane] = useState<ShipmentOrderLane | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // URLパラメータが変わったらフィルタを更新
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      loadingStartOn: startDate,
      loadingEndOn: endDate,
    }));
  }, [startDate, endDate]);

  // レーン一覧取得
  const {
    data: lanesData,
    isLoading,
    isError: isLanesError,
    error: lanesError,
    refetch: refetchLanes,
  } = useQueryShipmentOrderLanes(filters);

  // サマリー取得
  const {
    data: summary,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
    error: summaryError,
    refetch: refetchSummary,
  } = useQueryShipmentOrderLanesSummary({
    loadingStartOn: filters.loadingStartOn,
    loadingEndOn: filters.loadingEndOn,
    planningStatus: filters.planningStatus,
    deliveryMethod: filters.deliveryMethod,
    berthReservation: filters.berthReservation,
    keyword: filters.keyword,
  });

  // エラー時のトースト通知
  useEffect(() => {
    if (isLanesError && lanesError) {
      enqueueSnackbar(
        `レーン一覧の取得に失敗しました: ${lanesError.message || '不明なエラー'}`,
        { variant: 'error' }
      );
    }
  }, [isLanesError, lanesError, enqueueSnackbar]);

  useEffect(() => {
    if (isSummaryError && summaryError) {
      enqueueSnackbar(
        `サマリーの取得に失敗しました: ${summaryError.message || '不明なエラー'}`,
        { variant: 'error' }
      );
    }
  }, [isSummaryError, summaryError, enqueueSnackbar]);

  // 再取得関数
  const refetch = useCallback(() => {
    void refetchLanes();
    void refetchSummary();
  }, [refetchLanes, refetchSummary]);

  // レーン一覧データ
  const lanes = useMemo(() => lanesData?.lanes || [], [lanesData]);

  // ページネーション情報
  const pagination = useMemo(() => lanesData?.pagination || {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
  }, [lanesData]);

  // フィルタ更新
  const updateFilters = useCallback((newFilters: Partial<LaneFilterState>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      // フィルタが変わったらページを1に戻す（page自体の更新以外）
      page: 'page' in newFilters ? (newFilters.page ?? 1) : 1,
    }));
  }, []);

  // ページ変更
  const setPage = useCallback((page: number) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  // 詳細シートを開く
  const openDetail = useCallback((lane: ShipmentOrderLane) => {
    setSelectedLane(lane);
    setIsDetailOpen(true);
  }, []);

  // 詳細シートを閉じる
  const closeDetail = useCallback(() => {
    setIsDetailOpen(false);
    setSelectedLane(null);
  }, []);

  // 日付範囲変更（URLも更新）
  const handleDateRangeChange = useCallback((startOn: string, endOn: string) => {
    void navigate(`/lanes/${startOn}/${endOn}`);
  }, [navigate]);

  return {
    // データ
    lanes,
    summary,
    pagination,

    // フィルタ状態
    filters,

    // 詳細シート状態
    selectedLane,
    isDetailOpen,

    // ローディング状態
    isLoading,
    isSummaryLoading,

    // エラー状態
    isError: isLanesError || isSummaryError,
    refetch,

    // アクション
    updateFilters,
    setPage,
    openDetail,
    closeDetail,
    handleDateRangeChange,
  };
};
